import { useSelector } from '@/api/state';
import { Input } from '@payaca/ambrosia';
import Button from '@payaca/components/button/Button';
import { ButtonStyleVariant } from '@payaca/components/button/enums';
import FieldLabel from '@payaca/components/fieldLabel/FieldLabel';
import MiniLoader from '@payaca/components/miniLoader/MiniLoader';

import ValidatedFieldWrapper from '@payaca/components/validatedFieldWrapper/ValidatedFieldWrapper';
import ValidatedForm from '@payaca/components/validatedForm/ValidatedForm';
import { getLengthFieldValidator } from '@payaca/helpers/fieldValidationHelper';
import { currencyPrice } from '@payaca/helpers/financeHelper';
import * as jobPaymentsActions from '@payaca/store/jobPayments/jobPaymentsActions';
import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import { JobPayment } from '@payaca/types/jobPaymentTypes';
import UntitledIcon from '@payaca/untitled-icons';
import React, {
  FunctionComponent,
  useCallback,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';
import './ConfirmPaymentControl.css';

type Props = {
  jobPayment: JobPayment;
  customerName: string;
  dealReference: string;
  goToRecordPayment?: () => void;
  confirmPaymentCallback?: () => void;
};

const ConfirmPaymentControl: FunctionComponent<Props> = ({
  jobPayment,
  customerName,
  dealReference,
  goToRecordPayment,
  confirmPaymentCallback,
}: Props): JSX.Element => {
  const dispatch = useDispatch();
  const [hasSubmittedConfirmation, setHasSubmittedConfirmation] =
    useState(false);
  const isConfirmingJobPayment = useSelector((state) => {
    return state.jobPayments.isConfirmingJobPayment;
  });
  const isJobPaymentConfirmedSuccessfully = useSelector((state) => {
    return state.jobPayments.isJobPaymentConfirmedSuccessfully;
  });
  const account = useSelector(
    (state: any) => state.users.myProfile.accounts[0]
  );

  const initialFormState = useMemo(() => {
    return {
      jobPaymentId: jobPayment.id,
    };
  }, [jobPayment.id]);

  const onSubmit = useCallback(
    (jobPaymentId: number, note?: string, isPaymentReceived = true) => {
      dispatch(
        jobPaymentsActions.requestConfirmJobPayment(
          {
            jobPaymentId,
            isPaymentReceived,
            note,
          },
          () => confirmPaymentCallback?.()
        )
      );
      setHasSubmittedConfirmation(true);
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'confirmPaymentCallback'
    [dispatch]
  );

  const renderFormContents = useCallback(
    (
      isValid: boolean,
      formState: {
        [key: string]: any;
      },
      validationState: {
        [key: string]: FieldValidationResult;
      },
      touchedState: {
        [key: string]: boolean;
      },
      onFieldChange: (value: { [key: string]: any }) => void,
      onFieldTouch: (fieldName: string) => void
    ) => {
      return (
        <React.Fragment>
          <p>
            {customerName} has made a{' '}
            {jobPayment.isDepositPayment ? 'deposit payment' : 'payment'} of{' '}
            {currencyPrice(jobPayment.paymentValue, account.region)} via bank
            transfer towards Project #{dealReference}.
          </p>
          <ValidatedFieldWrapper
            validationResult={validationState.note}
            isTouched={touchedState.note || false}
          >
            <FieldLabel label="Internal note" />
            <Input
              name="note"
              value={formState.note}
              onChange={(value) => onFieldChange({ note: value })}
              onTouch={() => onFieldTouch('note')}
            />
          </ValidatedFieldWrapper>
          <div className="actions-container">
            <Button
              onClick={() =>
                onSubmit(formState.jobPaymentId, formState.note, true)
              }
              isDisabled={!isValid}
              iconAfter={'chevron-right'}
              styleVariant={ButtonStyleVariant.OUTSIZE}
            >
              {currencyPrice(jobPayment.paymentValue, account.region)} received
            </Button>
            <Button
              styleVariant={ButtonStyleVariant.ANCHOR}
              iconAfter={'x.3'}
              onClick={() =>
                onSubmit(formState.jobPaymentId, formState.note, false)
              }
            >
              Payment not received
            </Button>
            {goToRecordPayment && (
              <Button
                styleVariant={ButtonStyleVariant.ANCHOR}
                iconAfter={'chevron-right'}
                onClick={() => {
                  // fail the original payment made by customer
                  onSubmit(formState.jobPaymentId, undefined, false);
                  goToRecordPayment();
                }}
              >
                I have received another amount
              </Button>
            )}
          </div>
        </React.Fragment>
      );
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'onSubmit'
    [jobPayment, customerName, dealReference, goToRecordPayment, account]
  );

  return (
    <div className="confirm-payment-control">
      {!hasSubmittedConfirmation && (
        <ValidatedForm
          renderFormContents={renderFormContents}
          initialFormState={initialFormState}
          fieldValidators={{
            note: [getLengthFieldValidator({ max: 150 })],
          }}
        />
      )}
      {hasSubmittedConfirmation && isConfirmingJobPayment && (
        <div className="loader-container">
          <MiniLoader />
        </div>
      )}

      {hasSubmittedConfirmation && !isConfirmingJobPayment && (
        <div className="feedback-container">
          {isJobPaymentConfirmedSuccessfully ? (
            <React.Fragment>
              <UntitledIcon name="check" />
              <p>Success!</p>
            </React.Fragment>
          ) : (
            <p>Something went wrong updating this payment record</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ConfirmPaymentControl;
